import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {Fragment} from 'react'
import clsx from 'clsx'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {IconAmazonLogoSvg} from '~/icons/product-page/icon-amazon-logo-svg'
import {getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {objectToQueryString} from '~/utils/commons'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {BrgIconAppLogoLetter} from '~/icons/brg-icon-app-logo-letter'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import ProductCardBadge from '../badges/card-placement-badge'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'
import SaleEventBadge from '../badges/saleEventBadge'

export interface TopPicksCardProps {
  index: number
  product: Product
}

const TopPicksCard: FunctionComponent<TopPicksCardProps> = ({product, index}) => {
  const {locale, featureFlags, language, trackAnalytics, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    clickId,
    setShowCookieConsentGdpr,
    dataResponse: {productsCategory} = {} as SlugApiResponse,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()

  const {styles: stylesBydomain, siteName} = getDomainConfig(domain, locale)

  const {name, badge, asin, discount, brand, image, listScore, coupon, affiliateLink, isSellerProduct, brief, price} =
    product

  const mergedBriefParts = brief?.length ? brief.join('') : name

  const scoreFormatter = new Intl.NumberFormat(language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

  const productScore = scoreFormatter.format(listScore)

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)
  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })
  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportClickout = useSendPartnerProductClickout({
    product,
    index: index + 1,
    locale,
    listName: 'category-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })
  const reportWidgetClick = useReportWidgetClick()
  const [cardRef] = useReportItemSeen({
    item_list_id: productsCategory?.slug,
    item_name: name,
    item_source: 'list',
    partner_item_brand: brand ?? '',
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: isSellerProduct,
    price: price?.amount,
    currency: price?.currency,
    click_element_name: 'top-picks',
    index: index + 1,
  })
  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'top-picks-card',
      widget_place: `card-${index + 1}`,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: `${+productScore}`,
              bestRating: 10,
              worstRating: 0,
            },
            author: {
              '@type': 'Organization',
              name: siteName,
            },
            reviewBody: `${mergedBriefParts}`,
            itemReviewed: {
              '@type': 'Product',
              name: `${name}`,
              review: {
                '@context': 'https://schema.org/',
                '@type': 'Review',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: `${+productScore}`,
                  bestRating: 10,
                  worstRating: 0,
                },
                author: {
                  '@type': 'Organization',
                  name: siteName,
                },
                reviewBody: `${mergedBriefParts}`,
              },
            },
          }),
        }}
      ></script>

      <div className="flex flex-col gap-4">
        <ProductCardBadge
          className={clsx(
            'z-40 flex h-full w-fit items-center justify-center overflow-hidden rounded-lg',
            '[&>div>p]:px-3 [&>div>span]:text-xl [&>div>span]:font-semibold',
          )}
          index={index}
          badge={badge}
          coupon={coupon}
        />

        <h3 className="line-clamp-2 text-[20px] font-semibold capitalize text-[#363636] tablet:text-base">{name}</h3>

        <div
          ref={cardRef}
          className={clsx(
            'border-gray relative flex h-full min-h-[227px] w-full flex-row rounded-lg border bg-white shadow-product-page-section',
            'tablet:min-h-[170px] tablet:flex-row tablet:justify-evenly tablet:gap-4 tablet:px-1 mobile:justify-evenly',
          )}
        >
          {discount ? (
            <DiscountBadgeReorganized
              className={clsx(
                'absolute right-0 top-[18px] z-10 max-w-fit rounded-l-2xl px-3 py-0.5',
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
              )}
              discount={discount}
            />
          ) : null}

          <div className="relative flex w-full flex-col items-center justify-center tablet:w-fit tablet:gap-1">
            <img src={image} alt={name} className="size-44 object-scale-down tablet:size-24" />
            <span className="line-clamp-1 text-[20px] font-bold text-black tablet:text-sm">{brand}</span>
          </div>
          <div
            className={clsx(
              'ml-auto flex w-56 min-w-64 flex-col items-center justify-center gap-2 border-l border-l-gray-300',
              'tablet:ml-0 tablet:w-fit tablet:min-w-fit tablet:gap-1 tablet:border-none',
              'px-1',
            )}
          >
            <div className="relative flex flex-row gap-2">
              <label
                className={clsx(
                  'flex justify-center text-3xl',
                  stylesBydomain.LISTPAGE_BADGE_SCORE_TEXT,
                  'listpage_badge_score_text',
                )}
              >
                {productScore}
              </label>
              <div className="flex items-center text-xs leading-tight">
                {featureFlags?.useReorganizedCardLayout ? null : (
                  <BrgIconAppLogoLetter className="mr-1 h-[13px] w-[11px]" />
                )}
                <label>{translations.ProductCard.scoreSuffix}</label>
              </div>
            </div>
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
            >
              <IconAmazonLogoSvg className="mb-1 mt-1 h-5 object-scale-down" loading="lazy" />
            </a>
            <a
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
              className={clsx(
                'flex h-10 w-fit cursor-pointer items-center justify-center rounded-3xl px-5 text-center text-base uppercase tablet:h-8 tablet:text-sm',
                stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                'listpage_button_primary',
              )}
            >
              {translations.ProductCard.ctaText()}
            </a>
            {!!discount && (
              <SaleEventBadge
                redirectUrl={redirectUrl}
                handleCardClick={handleCardClick}
                brand={brand ?? ''}
                index={index}
                placement="top-picks-card"
              />
            )}
          </div>
        </div>
        <div>
          {brief?.map((breafPart, i) => (
            <Fragment key={`${breafPart.slice(0, 10)}-${i}`}>
              <p className="mt-2 text-lg font-light text-carbon-900 tablet:text-base">{breafPart}</p>
            </Fragment>
          ))}
        </div>
        <a
          href={redirectUrl}
          onClick={handleCardClick}
          aria-label={`view ${brand}`}
          target="_blank"
          rel="sponsored noreferrer"
          className={clsx(
            'mt-3 flex h-10 w-fit cursor-pointer items-center justify-center rounded-3xl px-5 text-center uppercase tablet:mt-0',
            stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
            'listpage_button_primary',
          )}
        >
          {translations.ProductCard.ctaText()}
        </a>
        <div className="mt-5 border-b border-gray-300" />
      </div>
    </>
  )
}

export default TopPicksCard
